import { useEffect, useRef, useCallback } from 'react';
import { useThemeMode } from '../contexts/ThemeContext';

/**
 * CustomCursor — branded cursor with context-aware states.
 * Uses refs + RAF for zero React re-renders on mousemove.
 * Hidden on touch devices and when prefers-reduced-motion is set.
 */
export default function CustomCursor() {
  const { isLight } = useThemeMode();
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const glowRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });       // ring interpolated position
  const glowPos = useRef({ x: -100, y: -100 });   // glow interpolated position (slower trail)
  const target = useRef({ x: -100, y: -100 });    // raw mouse position
  const raf = useRef(null);
  const visible = useRef(false);

  // Smooth follow — dot is immediate, ring is lerped, glow trails behind
  const loop = useCallback(() => {
    const ringLerp = 0.15;
    const glowLerp = 0.07;      // slower = softer trail

    pos.current.x += (target.current.x - pos.current.x) * ringLerp;
    pos.current.y += (target.current.y - pos.current.y) * ringLerp;
    glowPos.current.x += (target.current.x - glowPos.current.x) * glowLerp;
    glowPos.current.y += (target.current.y - glowPos.current.y) * glowLerp;

    if (dotRef.current) {
      dotRef.current.style.transform =
        `translate(${target.current.x}px, ${target.current.y}px) translate(-50%, -50%)`;
    }
    if (ringRef.current) {
      ringRef.current.style.transform =
        `translate(${pos.current.x}px, ${pos.current.y}px) translate(-50%, -50%)`;
    }
    if (glowRef.current) {
      glowRef.current.style.transform =
        `translate(${glowPos.current.x}px, ${glowPos.current.y}px) translate(-50%, -50%)`;
    }
    raf.current = requestAnimationFrame(loop);
  }, []);

  useEffect(() => {
    // Bail on touch devices or reduced-motion
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTouch || prefersReduced) return;

    // Inject body cursor:none
    document.body.style.cursor = 'none';

    const onMouseMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (!visible.current && dotRef.current && ringRef.current) {
        dotRef.current.style.opacity = '1';
        ringRef.current.style.opacity = '1';
        if (glowRef.current) glowRef.current.style.opacity = '1';
        visible.current = true;
      }
    };

    const onMouseDown = () => {
      if (dotRef.current) dotRef.current.style.transform += ' scale(0.6)';
    };

    const onMouseUp = () => {
      // transform is reset on next RAF, so just flash it
    };

    const onMouseEnterLink = () => {
      if (ringRef.current) {
        ringRef.current.style.width = '48px';
        ringRef.current.style.height = '48px';
        ringRef.current.style.background = 'rgba(99, 102, 241, 0.10)';
      }
    };

    const onMouseLeaveLink = () => {
      if (ringRef.current) {
        ringRef.current.style.width = '32px';
        ringRef.current.style.height = '32px';
        ringRef.current.style.background = 'transparent';
      }
    };

    // Delegate hover detection — listen on document
    const onMouseOver = (e) => {
      const el = e.target.closest('a, button, [role="button"], input, textarea, select, label');
      if (el) onMouseEnterLink();
    };
    const onMouseOut = (e) => {
      const el = e.target.closest('a, button, [role="button"], input, textarea, select, label');
      if (el) onMouseLeaveLink();
    };

    const onMouseLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = '0';
      if (ringRef.current) ringRef.current.style.opacity = '0';
      if (glowRef.current) glowRef.current.style.opacity = '0';
      visible.current = false;
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mouseover', onMouseOver, { passive: true });
    document.addEventListener('mouseout', onMouseOut, { passive: true });
    document.documentElement.addEventListener('mouseleave', onMouseLeave);

    raf.current = requestAnimationFrame(loop);

    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [loop]);

  // Touch/reduced-motion: render nothing
  if (typeof window !== 'undefined') {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTouch || prefersReduced) return null;
  }

  const shared = {
    position: 'fixed',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    zIndex: 99999,
    borderRadius: '50%',
    willChange: 'transform',
    transition: 'width 0.25s ease, height 0.25s ease, background 0.25s ease, opacity 0.3s ease',
    opacity: 0,
  };

  return (
    <>
      {/* Glow — soft radial trail behind dot & ring */}
      <div
        ref={glowRef}
        style={{
          ...shared,
          width: 140,
          height: 140,
          background: isLight
            ? 'radial-gradient(circle, rgba(99, 102, 241, 0.045) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(56, 189, 248, 0.07) 0%, transparent 70%)',
          zIndex: 99997,
        }}
      />
      {/* Dot — tracks cursor exactly */}
      <div
        ref={dotRef}
        style={{
          ...shared,
          width: 6,
          height: 6,
          background: isLight ? '#6366f1' : '#10b981',
        }}
      />
      {/* Ring — follows with lerp */}
      <div
        ref={ringRef}
        style={{
          ...shared,
          width: 32,
          height: 32,
          border: isLight
            ? '1.5px solid rgba(99, 102, 241, 0.5)'
            : '1.5px solid rgba(16, 185, 129, 0.5)',
          background: 'transparent',
        }}
      />
    </>
  );
}
